<template>
  <h3>Policies</h3>
  <div v-if="organizations.length >= 1" class="form-group">
    <OrganizationSelect v-bind="$attrs" :organizations="organizations" :organization-id="organizationId" />
  </div>
  <div id="policy_form" class="form-group">
    <h4>
      <span>{{ editId != null ? "Edit" : "Add" }} Policy</span>
      <span v-if="editId != null"> &middot; <a @click="reset">Cancel</a></span>
    </h4>
    <p class="infomsg">You can create multiple travel policies and assign your team members accordingly. If a booking is below policy limits, we will not request approval. The policy uses the same currency set as your organization default currency. The default policy is used for new team members who register automatically.</p>
    <div class="form" :class="{ loading: formIsLoading }">
      <Result v-model:error="formError" v-model:is-success="formIsSuccess" />
      <div class="form-section">
        <div class="form-row">
          <div class="form-col">
            <input v-model="formTitle" type="text" placeholder="Title (e.g. Staff or Executives)" maxlength="25" />
          </div>
          <div class="form-col">
            <select v-model="formApproverId">
              <option :value="null">Please select an Approver</option>
              <option v-for="m in dataMembers" :key="m.customer_id" :value="m.customer_id">
                {{ m.customer_name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h4>Approval</h4>
        <div class="radiolabel" :class="{ on: formIsApproveAlways === false }" @click="formIsApproveAlways = false">
          <div class="radio" />
          <label>Only require approval above budget</label>
        </div>
        <div class="radiolabel" :class="{ on: formIsApproveAlways === true }" @click="formIsApproveAlways = true">
          <div class="radio" />
          <label>Require approval for all bookings (not recommended)</label>
        </div>
      </div>
      <div class="form-section">
        <h4>Notification</h4>
        <div class="checkboxlabel" :class="{ on: formIsNotifyUpfront === true }" @click="formIsNotifyUpfront = !formIsNotifyUpfront">
          <div class="checkbox" />
          <label>Notify the approver when a booking is submitted</label>
        </div>
      </div>
      <div class="form-section policy-items">
        <h4>Budget: Accommodation</h4>
        <div class="form-row">
          <div class="form-col">
            <input v-model="formHotelMax" type="number" placeholder="250" maxlength="100" />
          </div>
          <div class="form-col">
            <span>{{ formCurrency }} maximum per room</span>
          </div>
        </div>
      </div>
      <div class="form-section policy-items">
        <h4>Budget: Flights</h4>
        <p class="infomsg">The Most Reasonable option is the flight with the lowest price, maximum cabin class allowed and fewest layovers for that route. For routes less than 6 hours, we consider flights with layovers shorter than 3 hours each. For longer routes, we consider flights with layovers shorter than 5 hours each.</p>
        <div class="form-row">
          <div class="form-col">
            <select v-model="formFlightWithinMrPercentage">
              <option value="10">10%</option>
              <option value="20">20%</option>
              <option value="30">30%</option>
              <option value="40">40%</option>
              <option value="50">50%</option>
              <option value="60">60%</option>
              <option value="70">70%</option>
              <option value="80">80%</option>
              <option value="90">90%</option>
              <option value="100">100%</option>
            </select>
          </div>
          <div class="form-col">
            <span>within the Most Reasonable option.</span>
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            <select v-model="formFlightMaxCabinShort">
              <option value="economy">Economy Only</option>
              <option value="premium-economy">Up to Premium Economy</option>
              <option value="business-first">Up to Business/First</option>
            </select>
          </div>
          <div class="form-col">
            <span>for routes 0-6 hours each way, and</span>
          </div>
          <div class="form-col">
            <select v-model="formFlightMaxUpgradeShort">
              <option v-for="n in listFlightMaxUpgrade(false)" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
          <div class="form-col">
            <span>{{ formCurrency }} for bags and upgrades.</span>
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            <select v-model="formFlightMaxCabinLong">
              <option value="economy">Economy Only</option>
              <option value="premium-economy">Up to Premium Economy</option>
              <option value="business-first">Up to Business/First</option>
            </select>
          </div>
          <div class="form-col">
            <span>for routes 6+ hours each way, and</span>
          </div>
          <div class="form-col">
            <select v-model="formFlightMaxUpgradeLong">
              <option v-for="n in listFlightMaxUpgrade(true)" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
          <div class="form-col">
            <span>{{ formCurrency }} for bags and upgrades.</span>
          </div>
        </div>
      </div>
      <Submit :is-loading="formIsLoading" :text="'Save Policy'" @click="save" />
    </div>
  </div>
  <div class="form-group">
    <h4>Policy List</h4>
    <table :class="{ loading: dataIsLoading }">
      <thead>
        <tr>
          <th>Name</th>
          <th>Approver</th>
          <th>Hotels</th>
          <th>Flights</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="dataPolicies.length == 0">
          <td colspan="5">No results</td>
        </tr>
        <tr v-for="p in dataPolicies" v-else :key="p.id">
          <td>{{ p.title }}</td>
          <td>{{ p.approver_name }}</td>
          <td>
            {{ p.hotel_summary }}
          </td>
          <td>
            {{ p.flight_summary }}
          </td>
          <td class="actions">
            <a v-if="!p.is_default" @click="actionDefault(p.id)">Set as Default</a>
            <span v-if="p.is_default">Default</span>
            <a @click="actionEdit(p.id)">Edit</a>
            <a @click="actionDelete(p.id)">Delete</a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import Result from "./common/FormResult.vue";
import Submit from "./common/FormSubmit.vue";
import OrganizationSelect from "../components/common/OrganizationSelect.vue";
import useApi from "../utils/apiCalls.js";

const props = defineProps(["organizations", "organizationId"]);
const emit = defineEmits(["error", "info", "scroll"]);

// Reactive state
const dataIsLoading = ref(false);
const dataMembers = ref([]);
const dataPolicies = ref([]);
const editId = ref(null);
const formCurrency = ref(null);
const formCurrencyRate = ref(null);
const formIsLoading = ref(false);
const formIsSuccess = ref(false);
const formError = ref(null);
const formTitle = ref(null);
const formApproverId = ref(null);
const formIsApproveAlways = ref(false);
const formIsNotifyUpfront = ref(false);
const formHotelMax = ref(null);
const formFlightWithinMrPercentage = ref(10);
const formFlightMaxCabinShort = ref("economy");
const formFlightMaxCabinLong = ref("economy");
const formFlightMaxUpgradeShort = ref(null);
const formFlightMaxUpgradeLong = ref(null);

// API Utilities
const { apiGetRequest, apiPutRequest, apiDeleteRequest, apiPostRequest } = useApi(emit);

// Watchers
watch(editId, newValue => {
  if (newValue != null) {
    const el = document.getElementById("policy_form");
    el.scrollIntoView({ behavior: "smooth" });
    loadPolicy();
  }
});

watch(
  () => props.organizationId,
  () => {
    reset();
    load();
    loadCurrency();
    loadMembers();
  }
);

// Lifecycle Hooks
onMounted(() => {
  if (props.organizationId != null) {
    load();
    loadCurrency();
    loadMembers();
  }
});

// Methods
const actionDefault = id => {
  dataIsLoading.value = true;

  apiPutRequest({
    path: `/organizations/${props.organizationId}/policies/${id}/default`,
    onSuccess: () => {
      load();
      dataIsLoading.value = false;
    },
    onError: error => {
      dataIsLoading.value = false;
      emit("error", error);
    },
  });
};

const actionDelete = id => {
  if (!confirm("Are you sure you want to delete this policy?")) {
    return;
  }

  dataIsLoading.value = true;

  apiDeleteRequest({
    path: `/organizations/${props.organizationId}/policies/${id}`,
    onSuccess: () => {
      load();
      dataIsLoading.value = false;
    },
    onError: error => {
      dataIsLoading.value = false;
      alert(`Error deleting policy: ${error}`);
    },
  });
};

const actionEdit = id => {
  formIsSuccess.value = false;
  formError.value = null;

  editId.value = id;
};

const listFlightMaxUpgrade = isLong => {
  const westCodes = ["USD", "AUD", "EUR", "GBP", "CAD", "CHF"];
  const westOptions = isLong ? [0, 100, 150, 200, 250, 500] : [0, 50, 100, 150, 200, 250];

  return westCodes.includes(formCurrency.value) ? westOptions : westOptions.map(x => Math.round(x * formCurrencyRate.value));
};

const listFlightMaxUpgradeDefault = isLong => {
  const westCodes = ["USD", "AUD", "EUR", "GBP", "CAD", "CHF"];
  const defaultVal = isLong ? 100 : 50;

  return westCodes.includes(formCurrency.value) ? defaultVal : Math.round(defaultVal * formCurrencyRate.value);
};

const load = () => {
  formIsLoading.value = true;

  apiGetRequest({
    path: `/organizations/${props.organizationId}/policies`,
    onSuccess: response => {
      dataPolicies.value = response;
      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const loadCurrency = () => {
  apiGetRequest({
    path: `/organizations/${props.organizationId}`,
    onSuccess: response => {
      formCurrency.value = response.currency;
      formCurrencyRate.value = response.currency_rate;
      formFlightMaxUpgradeShort.value = listFlightMaxUpgradeDefault(false);
      formFlightMaxUpgradeLong.value = listFlightMaxUpgradeDefault(true);
    },
    onError: error => {
      formError.value = error;
      emit("error", error);
    },
  });
};

const loadMembers = () => {
  formIsLoading.value = true;

  apiGetRequest({
    path: `/organizations/${props.organizationId}/members`,
    onSuccess: response => {
      dataMembers.value = response;
      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const loadPolicy = () => {
  formIsLoading.value = true;

  apiGetRequest({
    path: `/organizations/${props.organizationId}/policies/${editId.value}`,
    onSuccess: response => {
      formTitle.value = response.title;
      formApproverId.value = response.approver_id;
      formHotelMax.value = response.hotel_max;
      formFlightWithinMrPercentage.value = response.flight_within_mr_percentage;
      formFlightMaxCabinShort.value = response.flight_max_cabin_short;
      formFlightMaxCabinLong.value = response.flight_max_cabin_long;
      formFlightMaxUpgradeShort.value = response.flight_max_upgrade_short;
      formFlightMaxUpgradeLong.value = response.flight_max_upgrade_long;
      formIsApproveAlways.value = response.is_approve_always;
      formIsNotifyUpfront.value = response.is_notify_upfront;
      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const reset = () => {
  editId.value = null;
  formTitle.value = null;
  formApproverId.value = null;
  formHotelMax.value = null;
  formFlightMaxCabinShort.value = "economy";
  formFlightMaxUpgradeShort.value = null;
  formFlightMaxCabinLong.value = "economy";
  formFlightMaxUpgradeLong.value = null;
};

const save = () => {
  formIsLoading.value = true;

  const url = editId.value != null ? `/organizations/${props.organizationId}/policies/${editId.value}` : `/organizations/${props.organizationId}/policies`;

  const method = editId.value != null ? apiPutRequest : apiPostRequest;

  method({
    path: url,
    data: {
      title: formTitle.value,
      approver_id: formApproverId.value,
      hotel_max: Number(formHotelMax.value),
      flight_within_mr_percentage: Number(formFlightWithinMrPercentage.value),
      flight_max_cabin_short: formFlightMaxCabinShort.value,
      flight_max_cabin_long: formFlightMaxCabinLong.value,
      flight_max_upgrade_short: Number(formFlightMaxUpgradeShort.value),
      flight_max_upgrade_long: Number(formFlightMaxUpgradeLong.value),
      is_approve_always: formIsApproveAlways.value,
      is_notify_upfront: formIsNotifyUpfront.value,
    },
    onSuccess: () => {
      reset();
      load();
      formIsSuccess.value = true;
      formError.value = null;
      formIsLoading.value = false;
    },
    onError: error => {
      formIsSuccess.value = false;
      formError.value = error;
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};
</script>

<style lang="less">
#policy_form {
  .policy-items {
    .curr {
      text-align: right;
    }
  }

  .radiolabel {
    margin-bottom: 0.5rem;
  }
}
</style>
